/**
 * @file Turns loaded weapon `.glb` scenes into game-ready weapon objects.
 *
 * Two problems live here, and only here.
 *
 * The first is art direction: the source pack is flat matte greys, and this
 * game is a neon arena. Rather than repaint the models, every material is
 * cloned once and given the emissive channel the bloom pass feeds on, tinted
 * with the round that weapon fires — so a gun on the floor still reads as "the
 * red one" at a glance, exactly like the procedural block it replaces.
 *
 * The second is pivots. The pack centres each weapon on its bounding box and
 * the game orients things by yaw alone, so every model is wrapped in a group
 * that carries the art scale and the sit-on-the-floor offset. That wrapper is
 * what keeps {@link ../systems/RenderSystem.js} free to overwrite `scale` and
 * `rotation.y` on the outer object every frame.
 *
 * The library starts empty: prefabs are declared while the game is still
 * booting, but meshes are only built once a level asks for one — by which time
 * the loading state has filled this in. A weapon with no entry falls back to
 * the procedural block, so a failed download costs looks and nothing else.
 */

import * as THREE from 'three';
import { WEAPONS } from '../config/Weapons.js';
import {
  WEAPON_ART,
  WEAPON_MODEL_EMISSIVE,
  WEAPON_MODEL_SCALE,
  weaponModelKey
} from '../config/WeaponArt.js';

export class WeaponModelLibrary {
  constructor() {
    /**
     * Weapon key → prepared source object, cloned per instance.
     * @type {Map<string, THREE.Object3D>}
     * @private
     */
    this._sources = new Map();
  }

  /**
   * Takes the models an {@link ../core/AssetLoader.js AssetLoader} has fetched
   * and prepares one tinted, grounded source object per weapon. Safe to call on
   * every floor: weapons already prepared are left alone.
   *
   * @param {import('../core/AssetLoader.js').AssetLoader} assets Loader holding
   *   the models from {@link ../config/WeaponArt.js weaponModelManifest}.
   * @returns {void}
   */
  adopt(assets) {
    for (const [key, art] of Object.entries(WEAPON_ART)) {
      if (this._sources.has(key)) continue;

      const scene = assets.get(weaponModelKey(key));
      if (!scene) continue;

      this._sources.set(key, prepare(scene, key, art));
    }
  }

  /**
   * @param {string} key Weapon archetype key.
   * @returns {boolean} Whether a model is available for it.
   */
  has(key) {
    return this._sources.has(key);
  }

  /**
   * Builds a fresh instance of a weapon's model. Geometries and materials are
   * shared with every other instance — only the scene graph is duplicated.
   *
   * @param {string} key Weapon archetype key.
   * @returns {THREE.Object3D | null} A group whose own transform is untouched,
   *   so a caller may set position/rotation/scale on it freely. Null when the
   *   weapon has no model, which is the caller's cue to fall back.
   */
  create(key) {
    const source = this._sources.get(key);
    return source ? source.clone(true) : null;
  }

  /** @returns {void} */
  clear() {
    this._sources.clear();
  }
}

/**
 * @param {THREE.Object3D} scene Loaded glTF scene.
 * @param {string} key Weapon archetype key.
 * @param {import('../config/WeaponArt.js').WeaponArt} art Art entry.
 * @returns {THREE.Object3D} Wrapper carrying the prepared model.
 */
function prepare(scene, key, art) {
  const tint = WEAPONS[key]?.projectile?.color ?? 0xffffff;

  scene.traverse((node) => {
    if (!node.isMesh) return;
    node.castShadow = true;
    node.receiveShadow = false;
    node.material = tinted(node.material, tint);
  });

  // Centre the weapon on its own body before scaling, so the model spins about
  // the middle of the gun on the floor rather than about an arbitrary pivot.
  const bounds = new THREE.Box3().setFromObject(scene);
  const centre = bounds.getCenter(new THREE.Vector3());

  const inner = new THREE.Group();
  inner.add(scene);
  scene.position.sub(centre);
  scene.position.y += art.yOffset ?? 0;
  inner.scale.setScalar(art.scale ?? WEAPON_MODEL_SCALE);

  const wrapper = new THREE.Group();
  wrapper.name = `weapon-model:${key}`;
  wrapper.add(inner);
  return wrapper;
}

/**
 * @param {THREE.Material | THREE.Material[]} material Source material(s).
 * @param {number} tint Emissive colour.
 * @returns {THREE.Material | THREE.Material[]} Cloned, neon-graded copies.
 */
function tinted(material, tint) {
  if (Array.isArray(material)) return material.map((entry) => tinted(entry, tint));

  const copy = material.clone();
  if ('emissive' in copy) {
    copy.emissive = new THREE.Color(tint);
    copy.emissiveIntensity = WEAPON_MODEL_EMISSIVE;
  }
  // The original never reaches the GPU — the copy replaces it before anything
  // draws — but the loader's cache would otherwise hold it forever.
  material.dispose();
  return copy;
}
